import { Dashboard } from '@uppy/react';
import { useCallback, useState } from 'react';
// import Webcam from '@uppy/webcam';
import AvatarEditor from 'react-avatar-editor';
import { useTranslation } from 'react-i18next';
import { Button, Grid, Modal, Segment } from 'semantic-ui-react';

import { dataURLtoBlob } from '../../helpers/utils';
import { useUppy } from '../../hooks/useUppy';

const UPPY_OPTIONS = {
  restrictions: {
    maxFileSize: 3500000,
  },
};
const UPPY_SOURCE_CROP = 'crop';

export default function ProfilePictureUploader({
  title,
  onUploaded,
  uploadPath,
}) {
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState(false);
  const [rawImage, setRawImage] = useState(null);
  const [editor, setEditor] = useState(null);

  const onUploadedLocal = useCallback(
    ({ downloadUrl }) => {
      if (onUploaded) onUploaded(downloadUrl);
      clearPicture();
      setModalOpen(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onUploaded]
  );

  const onAdded = useCallback((result) => {
    if (result?.source === UPPY_SOURCE_CROP) {
      setRawImage(null);
    } else if (result?.data) {
      setRawImage(result.data);
    }
  }, []);

  const uppy = useUppy({
    uploadPath,
    onUploaded: onUploadedLocal,
    onAdded,
    options: UPPY_OPTIONS,
  });

  return (
    <Modal
      trigger={
        <Button
          primary
          icon="cloud upload"
          content={t('Upload')}
          className="round"
          onClick={(e) => {
            e.preventDefault();
            setModalOpen(true);
          }}
        />
      }
      closeIcon
      open={modalOpen}
      size="small"
      header={title || t('Profile Picture Uploader')}
      closeOnEscape={false}
      closeOnDimmerClick={false}
      onClose={() => {
        setModalOpen(false);
        clearPicture();
        uppy?.reset();
      }}
      content={rawImage ? renderCropWindow() : renderDashboard()}
    />
  );

  function renderCropWindow() {
    return (
      <Segment placeholder>
        <Grid>
          <Grid.Row verticalAlign="middle">
            <Grid.Column width={10}>
              <Segment basic textAlign="center">
                {rawImage && (
                  <AvatarEditor
                    ref={(_editor) => setEditor(_editor)}
                    image={rawImage}
                    width={300}
                    height={300}
                    border={50}
                    scale={1}
                    rotate={0}
                  />
                )}
              </Segment>
            </Grid.Column>
            <Grid.Column width={6}>
              <Button.Group fluid>
                <Button
                  primary
                  content={t('Crop')}
                  onClick={(e) => {
                    e.preventDefault();

                    // Generate blob from the editor canvas
                    const blob = dataURLtoBlob(editor.getImage().toDataURL());

                    clearPicture();
                    const previewUrl = window.URL.createObjectURL(blob);

                    // Add cropped file to Uppy
                    uppy.addFile({
                      type: blob.type,
                      data: blob,
                      preview: previewUrl,
                      source: UPPY_SOURCE_CROP,
                    });
                  }}
                />
                <Button.Or />
                {rawImage && (
                  <Button
                    content={t('Clear')}
                    onClick={(e) => {
                      e.preventDefault();
                      clearPicture();
                    }}
                  />
                )}
              </Button.Group>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>
    );
  }

  function renderDashboard() {
    if (!uppy) return null;
    return (
      <Dashboard
        uppy={uppy}
        hideProgressAfterFinish
        inline
        proudlyDisplayPoweredByUppy={false}
        onRequestClose={onClose}
      />
    );
  }
  function onClose(e) {
    if (e) e.preventDefault();
    clearPicture();
    setModalOpen(false);
  }
  function clearPicture() {
    if (uppy) {
      const currentFiles = uppy.getFiles();
      currentFiles.forEach((currentFile) => uppy.removeFile(currentFile.id));
    }
    setRawImage(null);
  }
}
